import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import MoodSelector from "./MoodSelector";
import ReflectionPrompt from "./ReflectionPrompt";
import { CheckInData, ReflectionAnalysis } from "@/types/user";
import { insightsService } from "@/services/insightsService";
import { wellnessService } from "@/services/wellnessService";
import { useToast } from "@/components/ui/use-toast";
import {
  Calendar as CalendarIcon,
  CheckSquare,
  X,
  Plus,
  Clock,
  Link,
  Loader2,
  AlertCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { v4 as uuidv4 } from "uuid";
import { useCalendarIntegrations } from "@/hooks/useCalendarIntegrations";
import { connectCalendarProvider } from "@/services/calendarService";
import { meetingsService } from "@/services/meetingsService";
import { useAuth } from "@/contexts/AuthContext";

type EnhancedCheckInProps = {
  onSubmit: (data: CheckInData, insights: any | null) => void;
  isSubmitting?: boolean;
  existingCheckIn?: CheckInData | null;
  onDateChange?: (date: Date) => void;
  selectedCheckInDate?: Date;
};

const EnhancedCheckIn = ({
  onSubmit,
  isSubmitting = false,
  existingCheckIn,
  onDateChange,
  selectedCheckInDate,
}: EnhancedCheckInProps) => {
  const { user } = useUser();
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const { calendarIntegrations } = useCalendarIntegrations(user?.id);
  // Initialize with neutral values for new check-ins, only populate if editing existing
  const [mood, setMood] = useState<number>(existingCheckIn?.mood ?? 3);
  const [energy, setEnergy] = useState<number>(existingCheckIn?.energy ?? 50);
  const [focus, setFocus] = useState<number>(existingCheckIn?.focus ?? 50);
  const [motivation, setMotivation] = useState<number>(
    existingCheckIn?.motivation ?? 50
  );
  const [taskInput, setTaskInput] = useState<string>("");
  const [tasks, setTasks] = useState<string[]>(existingCheckIn?.tasks || []);
  const [wins, setWins] = useState<string>(existingCheckIn?.wins || "");
  const [blockers, setBlockers] = useState<string>(
    existingCheckIn?.blockers || ""
  );
  const [reflection, setReflection] = useState<string>(
    existingCheckIn?.reflection || ""
  );
  const [prompt, setPrompt] = useState<string>("");
  const [insights, setInsights] = useState<ReflectionAnalysis | null>(null);
  const [isAnalyzingReflection, setIsAnalyzingReflection] = useState(false);
  const [step, setStep] = useState<number>(() => {
    // Try to restore step from sessionStorage for this session
    const savedStep = sessionStorage.getItem('checkInStep');
    return savedStep ? parseInt(savedStep, 10) : 1;
  });
  const [hasCalendarIntegration, setHasCalendarIntegration] =
    useState<boolean>(false);
  const [calendarEvents, setCalendarEvents] = useState<
    { id: string; title: string; time: string }[]
  >([]);
  const [showCalendarEvents, setShowCalendarEvents] = useState<boolean>(true);
  const [needsHelp, setNeedsHelp] = useState<boolean>(
    existingCheckIn?.helpNeeded || false
  );
  const [helpWith, setHelpWith] = useState<string>(
    existingCheckIn?.helpWith || ""
  );
  const [selectedDate, setSelectedDate] = useState<Date>(selectedCheckInDate || new Date());
  const [userHasTypedReflection, setUserHasTypedReflection] = useState<boolean>(false);
  const [userHasModifiedTasks, setUserHasModifiedTasks] = useState<boolean>(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [isConnectingCalendar, setIsConnectingCalendar] = useState<boolean>(false);
  const [connectedProvider, setConnectedProvider] = useState<"google" | "outlook" | "apple" | null>(null);

  // Track the last loaded check-in ID to prevent unnecessary resets
  const [lastLoadedCheckInId, setLastLoadedCheckInId] = useState<string | null>(null);

  // Personalized questions state
  const [personalizedQuestions, setPersonalizedQuestions] = useState<any | null>(null);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState<boolean>(false);

  // Update form values when existingCheckIn changes or when switching to a date with no check-in
  useEffect(() => {
    // Only update if the check-in ID has actually changed
    if (existingCheckIn?.id === lastLoadedCheckInId && existingCheckIn !== null) {
      return; // Skip update if same check-in
    }

    if (existingCheckIn) {
      // Load existing check-in data
      setMood(existingCheckIn.mood ?? 3);
      setEnergy(existingCheckIn.energy ?? 50);
      setFocus(existingCheckIn.focus ?? 50);
      setMotivation(existingCheckIn.motivation ?? 50);
      setTasks(existingCheckIn.tasks || []);
      setWins(existingCheckIn.wins || "");
      setBlockers(existingCheckIn.blockers || "");
      setReflection(existingCheckIn.reflection || "");
      setNeedsHelp(existingCheckIn.helpNeeded || false);
      setHelpWith(existingCheckIn.helpWith || "");
      // Clear old insights when loading different check-in data
      setInsights(null);
      setPrompt("");
      setLastLoadedCheckInId(existingCheckIn.id);
    } else if (lastLoadedCheckInId !== null) {
      // Only reset to default values if we're transitioning from a check-in to no check-in
      setMood(3); // Neutral mood
      setEnergy(50); // Middle energy
      setFocus(50); // Middle focus
      setMotivation(50); // Middle motivation
      setTasks([]); // Empty tasks
      setWins(""); // Empty wins
      setBlockers(""); // Empty blockers
      setReflection(""); // Empty reflection
      setNeedsHelp(false); // No help needed
      setHelpWith(""); // Empty help text
      setInsights(null); // Clear insights
      setPrompt(""); // Clear prompt
      setStep(1); // Reset to step 1
      sessionStorage.setItem('checkInStep', '1'); // Update stored step
      setLastLoadedCheckInId(null);
    }
  }, [existingCheckIn, lastLoadedCheckInId]); // Track both the check-in and last loaded ID

  // Handle date selection changes
  useEffect(() => {
    if (onDateChange && selectedDate) {
      onDateChange(selectedDate);
    }
  }, [selectedDate, onDateChange]);

  // Save step to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('checkInStep', step.toString());
  }, [step]);

  // Clear saved step when component unmounts or when starting a new check-in
  useEffect(() => {
    return () => {
      // Only clear if we're not in the middle of a check-in process
      if (step === 1) {
        sessionStorage.removeItem('checkInStep');
      }
    };
  }, []);

  useEffect(() => {
    // Check if user has at least one calendar integration
    const hasIntegration = calendarIntegrations 
      ? Object.values(calendarIntegrations).some((val) => val)
      : false;
    setHasCalendarIntegration(hasIntegration);

    // Get events for the selected date (demonstration purposes)
    fetchEventsForSelectedDate(selectedDate);
  }, [calendarIntegrations, selectedDate]);

  const fetchEventsForSelectedDate = async (date: Date) => {
    if (!user?.id) {
      setCalendarEvents([]);
      return;
    }

    try {
      // Check for calendar integrations - returns data directly
      console.log('[Calendar] Checking integrations for user:', user.id);
      const integrations = await meetingsService.getCalendarIntegrations();
      const integrationsArray = Array.isArray(integrations) ? integrations : [];
      console.log('[Calendar] Found integrations:', integrationsArray.length);
      
      if (integrationsArray.length > 0) {
        setHasCalendarIntegration(true);
        // Set the connected provider
        setConnectedProvider(integrationsArray[0].provider as "google" | "outlook" | "apple");
        setCalendarError(null); // Clear any old error when integration exists
      } else {
        setHasCalendarIntegration(false);
        setCalendarEvents([]);
        setCalendarError(null); // Clear error when no integration (not an error state)
        return;
      }

      // Format dates as YYYY-MM-DD (backend validation requires this format)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      console.log('[Calendar] Fetching events for date:', dateStr);
      // Fetch calendar events from backend - returns data directly
      const events = await meetingsService.getCalendarEvents({
        startDate: dateStr,
        endDate: dateStr,
      });

      // Handle various response formats from API
      const eventsArray = Array.isArray(events) ? events : (events?.events || []);
      console.log('[Calendar] Fetched events:', eventsArray.length);
      
      if (eventsArray.length > 0) {
        const transformedEvents = eventsArray
          .filter((event: any) => {
            // Filter out events with invalid startTime
            // Handle both camelCase and snake_case from API
            const startTime = event.startTime || event.start_time;
            if (!startTime) {
              console.warn('[Calendar] Event missing startTime:', event);
              return false;
            }
            
            const date = new Date(startTime);
            if (isNaN(date.getTime())) {
              console.warn('[Calendar] Event has invalid startTime:', startTime, event);
              return false;
            }
            
            return true;
          })
          .map((event: any, index: number) => {
            // Handle both camelCase and snake_case from API
            const startTime = event.startTime || event.start_time;
            const date = new Date(startTime);
            
            return {
          id: event.id || event.externalId || `event-${index}`,
              title: event.title || 'Untitled Event',
              time: format(date, "h:mm a"),
            };
          });

        setCalendarEvents(transformedEvents);
        setCalendarError(null);
      } else {
        setCalendarEvents([]);
        setCalendarError(null); // No events is not an error
      }
    } catch (error: any) {
      console.error("[Calendar] Error fetching events:", error);
      console.error("[Calendar] Error details:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      setCalendarEvents([]);
      // Set appropriate error message based on error type
      if (error.message?.includes('Token expired') || error.message?.includes('401') || error.response?.status === 401) {
        setCalendarError('Your calendar connection has expired. Please reconnect.');
      } else if (error.response?.status === 404) {
        setCalendarError(null); // No calendar integration found - not an error
        setHasCalendarIntegration(false);
      } else {
        setCalendarError(error.message || 'Unable to load calendar events. Please reconnect your calendar.');
      }
    }
  };

  const addTask = () => {
    if (taskInput.trim()) {
      const newTask = taskInput.trim();
      if (!tasks.includes(newTask)) {
        const newTasks = [...tasks, newTask];
        setUserHasModifiedTasks(true);
        setTasks(newTasks);
        setTaskInput("");
        toast({
          title: "Task added",
          description: `Added "${newTask}" to your task list`,
        });
      } else {
        toast({
          title: "Task already exists",
          description: "This task is already in your list",
          variant: "destructive",
        });
      }
    } else {
      toast({
        title: "Empty task",
        description: "Please enter a task before adding",
        variant: "destructive",
      });
    }
  };

  const removeTask = (index: number) => {
    const newTasks = tasks.filter((_, i) => i !== index);
    setUserHasModifiedTasks(true);
    setTasks(newTasks);
    toast({
      title: "Task removed",
      description: "Task removed from your list",
    });
  };

  const addEventAsTask = (event: {
    id: string;
    title: string;
    time: string;
  }) => {
    const eventTask = `${event.title} (${event.time})`;
    if (!tasks.includes(eventTask)) {
      setUserHasModifiedTasks(true);
      setTasks([...tasks, eventTask]);
      toast({
        title: "Event added",
        description: `Added "${event.title}" to your tasks`,
      });
    }
  };

  const loadPersonalizedQuestions = async () => {
    if (!user?.id) return;
    
    setIsLoadingQuestions(true);
    try {
      console.log('[PersonalizedQuestions] Loading for user:', user.id);
      const questions = await wellnessService.getPersonalizedQuestions(
        mood,
        energy
      );
      
      // Validate response has actual questions
      if (questions && (questions.mood?.length > 0 || questions.tasks?.length > 0 || questions.reflection?.length > 0)) {
        console.log('[PersonalizedQuestions] Loaded successfully:', {
          mood: questions.mood?.length || 0,
          tasks: questions.tasks?.length || 0,
          reflection: questions.reflection?.length || 0
        });
        setPersonalizedQuestions(questions);
      } else {
        console.warn('[PersonalizedQuestions] Empty response, using default questions');
        setPersonalizedQuestions(null); // Will use default UI questions
      }
    } catch (error: any) {
      console.error('Error loading personalized questions:', error);
      // Show user-friendly feedback instead of silent fail
      toast({
        title: "Using default questions",
        description: "Personalized questions are temporarily unavailable",
        variant: "default",
      });
      setPersonalizedQuestions(null); // Will use default UI questions
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Load personalized questions when component mounts or when user changes
  useEffect(() => {
    if (user?.id && !existingCheckIn) {
      loadPersonalizedQuestions();
    }
  }, [user?.id]);

  const handleReflectionAnalysis = async (reflectionText: string) => {
    if (!reflectionText.trim() || !user) return;

    setIsAnalyzingReflection(true);
    try {
      // Get user's historical data for better AI analysis - returns array directly
      console.log('[AI] Starting reflection analysis');
      const checkInsData = await wellnessService.getCheckIns({ limit: 7 });
      const previousCheckIns = Array.isArray(checkInsData) ? checkInsData : [];
      
      // Extract reflections from check-ins
      const previousReflections = previousCheckIns
        .filter((ci: any) => ci.reflection && ci.reflection.trim().length > 0)
        .slice(0, 5)
        .map((ci: any) => ci.reflection);

      const analysisResult = await insightsService.analyzeReflection({
        reflection: reflectionText,
        moodScore: previousCheckIns?.[0]?.mood,
        energyLevel: previousCheckIns?.[0]?.energy,
        focusLevel: previousCheckIns?.[0]?.focus,
      });
      
      // Validate response has actual insights
      if (analysisResult && (analysisResult.keyThemes?.length > 0 || analysisResult.insightSummary)) {
        console.log('[AI] Analysis successful:', analysisResult);
        setInsights(analysisResult);
        toast({
          title: "✨ AI Analysis Complete",
          description: "Personalized insights based on your patterns and history.",
        });
      } else {
        console.warn('[AI] Empty analysis result, using fallback');
        // Set fallback insights when response is empty
        setInsights({
          sentimentScore: 0,
          keyThemes: [],
          insightSummary: "Reflection recorded. Continue checking in for personalized insights.",
          focusAreas: [],
          suggestedActions: [],
        });
      }
    } catch (error: any) {
      console.error('Error analyzing reflection:', error);
      console.error('Error details:', { message: error.message, response: error.response?.data });
      // Show fallback insights instead of error - user's reflection is still saved
      setInsights({
        sentimentScore: 0,
        keyThemes: [],
        insightSummary: "Analysis temporarily unavailable. Your reflection has been saved and will be analyzed later.",
        focusAreas: [],
        suggestedActions: [],
      });
      // Don't show destructive toast since we're providing fallback
      toast({
        title: "Using basic analysis",
        description: "AI insights will be available soon.",
        variant: "default",
      });
    } finally {
      setIsAnalyzingReflection(false);
    }
  };

  // Analyze reflection when user stops typing (debounced)
  useEffect(() => {
    if (!reflection.trim()) {
      setInsights(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      handleReflectionAnalysis(reflection);
    }, 2000); // Wait 2 seconds after user stops typing

    return () => clearTimeout(timeoutId);
  }, [reflection, user]);

  const connectCalendar = async (provider: "google" | "outlook" | "apple") => {
    if (!authUser?.id || !user?.id) {
      toast({
        title: "Authentication required",
        description: "Please sign in to connect your calendar.",
        variant: "destructive",
      });
      return;
    }

    // Only Google and Apple are currently supported
    if (provider === 'outlook') {
      toast({
        title: "Not yet supported",
        description: "Outlook calendar integration is coming soon.",
        variant: "default",
      });
      return;
    }

    setIsConnectingCalendar(true);
    setCalendarError(null);
    
    try {
      const success = await connectCalendarProvider(provider as 'google' | 'apple', '', user.id);
      setIsConnectingCalendar(false);
      
      if (success) {
        toast({
          title: "Calendar connected",
          description: `Successfully connected to ${provider} calendar!`,
        });
        // Refresh calendar integrations by reloading
        window.location.reload();
      } else {
        toast({
          title: "Connection failed",
          description: `Failed to connect to ${provider} calendar. Please try again.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
      setIsConnectingCalendar(false);
      toast({
        title: "Connection failed",
        description: `Failed to connect to ${provider} calendar. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const reconnectCalendar = async (provider: "google" | "outlook" | "apple") => {
    if (!authUser?.id || !user?.id) {
      toast({
        title: "Authentication required",
        description: "Please sign in to reconnect your calendar.",
        variant: "destructive",
      });
      return;
    }

    // Only Google and Apple are currently supported
    if (provider === 'outlook') {
      toast({
        title: "Not yet supported",
        description: "Outlook calendar integration is coming soon.",
        variant: "default",
      });
      return;
    }

    setIsConnectingCalendar(true);
    setCalendarError(null);
    
    try {
      const success = await connectCalendarProvider(provider as 'google' | 'apple', '', user.id);
      setIsConnectingCalendar(false);
      
      if (success) {
        toast({
          title: "Calendar reconnected",
          description: `Successfully reconnected to ${provider} calendar!`,
        });
        // Refresh calendar integrations by reloading
        window.location.reload();
      } else {
        toast({
          title: "Reconnection failed",
          description: `Failed to reconnect to ${provider} calendar. Please try again.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error reconnecting calendar:', error);
      setIsConnectingCalendar(false);
      toast({
        title: "Reconnection failed",
        description: `Failed to reconnect to ${provider} calendar. Please try again.`,
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to submit a check-in.",
        variant: "destructive",
      });
      return;
    }

    // Format date consistently
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateFormatted = `${year}-${month}-${day}`;

    const checkInData: CheckInData = {
      id: existingCheckIn?.id || uuidv4(),
      userId: user.id,
      date: dateFormatted,
      mood,
      energy,
      focus,
      motivation,
      tasks,
      wins: wins.trim(),
      blockers: blockers.trim(),
      reflection: reflection.trim(),
      helpNeeded: needsHelp,
      helpWith: needsHelp ? helpWith.trim() : "",
    };

    // Note: Removed update_checkin_completion RPC call as it doesn't exist in the database
    // Check-in completion status is handled by the presence of check-in data

    onSubmit(checkInData, insights);
    
    // Clear saved step after successful submission
    sessionStorage.removeItem('checkInStep');
  };

  const nextStep = () => {
    // Validate current step before proceeding
    if (step === 1) {
      // Validate step 1 - mood/energy required (removed validation since sliders have default values)
      // Users can proceed with default values if they want
    } else if (step === 2) {
      // Validate step 2 - no validation required for tasks/wins/blockers
      // Users can proceed without adding tasks or filling out wins/blockers
    }

    if (step < 3) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <Card className="shadow-md animate-in fade-in-50">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-2xl">
              {selectedDate.toDateString() === new Date().toDateString() 
                ? "Daily Check-in" 
                : `Check-in for ${format(selectedDate, "MMMM d, yyyy")}`
              }
            </CardTitle>
            <CardDescription>
              {selectedDate.toDateString() === new Date().toDateString()
                ? (existingCheckIn ? "Edit your check-in for today" : "How are you doing today?")
                : existingCheckIn 
                  ? `Editing check-in from ${format(selectedDate, "EEEE, MMMM d")}`
                  : `No check-in found for ${format(selectedDate, "EEEE, MMMM d")}`
              }
            </CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            {!existingCheckIn && user?.id && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-9"
                onClick={loadPersonalizedQuestions}
                disabled={isLoadingQuestions}
              >
                {isLoadingQuestions ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh
              </Button>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(selectedDate, "PPP")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="flex items-center justify-center mt-2">
          <div className="flex w-full max-w-xs">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                    i < step
                      ? "bg-primary text-primary-foreground"
                      : i === step
                      ? "border-2 border-primary text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {i}
                </div>
                <div className="text-xs text-muted-foreground">
                  {i === 1 ? "Status" : i === 2 ? "Tasks" : "Reflection"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">

        {/* Personalized Context Prompt */}
        {personalizedQuestions?.contextualPrompt && !existingCheckIn && (
          <div className="rounded-lg bg-gradient-to-r from-primary/10 via-primary/5 to-background border border-primary/20 p-4 animate-in fade-in-50 flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-sm text-foreground/90 leading-relaxed">
              {personalizedQuestions.contextualPrompt}
            </p>
          </div>
        )}

        {/* Step 1: Status and Mood */}
        {step === 1 && (
          <div className="space-y-6 animate-in fade-in-50">
            {/* Personalized mood questions */}
            {personalizedQuestions?.mood && personalizedQuestions.mood.length > 0 && (
              <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-muted">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Sparkles className="h-4 w-4" />
                  Personalized for you
                </div>
                {personalizedQuestions.mood.map((q) => (
                  <div key={q.id} className="space-y-1">
                    <p className="text-sm font-medium">{q.question}</p>
                    {q.subtext && (
                      <p className="text-xs text-muted-foreground">{q.subtext}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-base">How are you feeling today?</Label>
              <MoodSelector 
                value={mood} 
                onChange={setMood} 
              />
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="energy">Energy Level</Label>
                  <span className="text-sm text-muted-foreground">
                    {energy}%
                  </span>
                </div>
                <Slider
                  id="energy"
                  value={[energy]}
                  onValueChange={(v) => setEnergy(v[0])}
                  max={100}
                  step={1}
                  className="py-2"
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="focus">Focus Level</Label>
                  <span className="text-sm text-muted-foreground">
                    {focus}%
                  </span>
                </div>
                <Slider
                  id="focus"
                  value={[focus]}
                  onValueChange={(v) => setFocus(v[0])}
                  max={100}
                  step={1}
                  className="py-2"
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label htmlFor="motivation">Motivation Level</Label>
                  <span className="text-sm text-muted-foreground">
                    {motivation}%
                  </span>
                </div>
                <Slider
                  id="motivation"
                  value={[motivation]}
                  onValueChange={(v) => setMotivation(v[0])}
                  max={100}
                  step={1}
                  className="py-2"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 and 3: Tasks and Reflection */}
        {step === 2 && (
          <div className="space-y-6 animate-in fade-in-50">
            {/* Personalized task questions */}
            {personalizedQuestions?.tasks && personalizedQuestions.tasks.length > 0 && (
              <div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-muted">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Sparkles className="h-4 w-4" />
                  Personalized guidance
                </div>
                {personalizedQuestions.tasks.map((q) => (
                  <div key={q.id} className="space-y-1">
                    <p className="text-sm font-medium">{q.question}</p>
                    {q.subtext && (
                      <p className="text-xs text-muted-foreground">{q.subtext}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-base">
                What are you working on today?
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a task or priority"
                  value={taskInput}
                  onChange={(e) => setTaskInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addTask();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addTask}
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Calendar Events Section - Improved */}
              <div className="mt-6 rounded-xl border shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-primary/5 to-primary/10 px-4 py-3 border-b">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <CalendarIcon className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold">Calendar Events</h3>
                        <p className="text-xs text-muted-foreground">
                          {selectedDate.toDateString() === new Date().toDateString() 
                            ? "Today's schedule" 
                            : format(selectedDate, "EEEE, MMMM d")
                          }
                        </p>
                      </div>
                    </div>
                    {!hasCalendarIntegration && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs hover:bg-primary/10"
                        onClick={() => connectCalendar("google")}
                      >
                        <Link className="h-3 w-3 mr-1" />
                        Connect
                      </Button>
                    )}
                  </div>
                </div>

                {hasCalendarIntegration ? (
                  <div className="p-3">
                    {calendarError ? (
                      <div className="p-6 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800">
                        <div className="flex flex-col items-center text-center space-y-4">
                          <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center">
                            <AlertCircle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-sm font-medium text-orange-900 dark:text-orange-200">
                              Calendar Connection Issue
                            </h4>
                            <p className="text-sm text-orange-700 dark:text-orange-300">
                              {calendarError}
                            </p>
                          </div>
                          <Button 
                            onClick={() => reconnectCalendar(connectedProvider || "google")}
                            disabled={isConnectingCalendar}
                            size="sm"
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                          >
                            {isConnectingCalendar ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Reconnecting...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Reconnect {connectedProvider ? connectedProvider.charAt(0).toUpperCase() + connectedProvider.slice(1) : "Google"} Calendar
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : calendarEvents.length > 0 ? (
                      <div className="space-y-2">
                        {calendarEvents.map((event) => (
                          <div
                            key={event.id}
                            className="group flex items-center justify-between p-3 bg-card rounded-lg border hover:border-primary/20 hover:bg-primary/5 transition-all duration-200"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="relative">
                                <div className="w-2 h-2 bg-primary rounded-full"></div>
                                <div className="absolute top-2 left-0 w-2 h-8 bg-gradient-to-b from-primary/40 to-transparent rounded-full"></div>
                              </div>
                              <div>
                                <p className="font-medium text-sm text-foreground">
                                  {event.title}
                                </p>
                                <p className="text-xs text-muted-foreground flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {event.time}
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              onClick={() => addEventAsTask(event)}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add task
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 rounded-full bg-muted/30 mx-auto mb-4 flex items-center justify-center">
                          <Clock className="h-8 w-8 text-muted-foreground/60" />
                        </div>
                        <p className="text-sm text-muted-foreground font-medium">No events scheduled</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedDate.toDateString() === new Date().toDateString() 
                            ? "Your calendar is clear for today" 
                            : `No events on ${format(selectedDate, "MMMM d")}`
                          }
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="text-center space-y-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 mx-auto flex items-center justify-center">
                        <Link className="h-6 w-6 text-primary" />
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium">Connect Your Calendar</h4>
                        <p className="text-xs text-muted-foreground max-w-[250px] mx-auto">
                          Sync your calendar to see today's events and easily add them to your task list
                        </p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => connectCalendar("google")}
                          className="h-9 hover:border-primary/50"
                          disabled={isConnectingCalendar}
                        >
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Google Calendar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => connectCalendar("outlook")}
                          className="h-9 hover:border-primary/50"
                          disabled={isConnectingCalendar}
                        >
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                            <path fill="#0078D4" d="M12 1L2 6.5V17.5L12 23L22 17.5V6.5L12 1ZM12 16.5C9.24 16.5 7 14.26 7 11.5C7 8.74 9.24 6.5 12 6.5C14.76 6.5 17 8.74 17 11.5C17 14.26 14.76 16.5 12 16.5ZM12 8.5C10.34 8.5 9 9.84 9 11.5C9 13.16 10.34 14.5 12 14.5C13.66 14.5 15 13.16 15 11.5C15 9.84 13.66 8.5 12 8.5Z"/>
                          </svg>
                          Outlook
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="space-y-2">
                {tasks.length > 0 ? (
                  <div className="space-y-2">
                    {tasks.map((task, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 border rounded bg-muted/20"
                      >
                        <div className="flex items-center gap-2">
                          <CheckSquare className="h-4 w-4 text-muted-foreground" />
                          <span>{task}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => removeTask(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-4 border border-dashed rounded-md text-muted-foreground">
                    No tasks added yet
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="wins">Any wins or progress to share?</Label>
                <Textarea
                  id="wins"
                  placeholder="What went well? What progress did you make?"
                  value={wins}
                  onChange={(e) => setWins(e.target.value)}
                  className="min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="blockers">Any blockers or challenges?</Label>
                <Textarea
                  id="blockers"
                  placeholder="What's holding you back? How can your team help?"
                  value={blockers}
                  onChange={(e) => setBlockers(e.target.value)}
                  className="min-h-[80px]"
                />
              </div>

              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="needsHelp" className="font-medium text-base">
                    Do you need help with anything?
                  </Label>
                  <Switch
                    id="needsHelp"
                    checked={needsHelp}
                    onCheckedChange={setNeedsHelp}
                  />
                </div>

                {needsHelp && (
                  <Textarea
                    placeholder="What do you need help with? Be specific so your team can assist you."
                    value={helpWith}
                    onChange={(e) => setHelpWith(e.target.value)}
                    className="mt-2 min-h-[80px]"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Reflection */}
        {step === 3 && (
          <div className="space-y-6 animate-in fade-in-50">
            {/* Personalized reflection questions */}
            {personalizedQuestions?.reflection && personalizedQuestions.reflection.length > 0 && (
              <div className="space-y-4 p-4 rounded-lg bg-gradient-to-br from-purple-50/50 via-blue-50/50 to-background border border-purple-200/50">
                <div className="flex items-center gap-2 text-sm font-medium text-purple-700">
                  <Sparkles className="h-4 w-4" />
                  Reflection prompts crafted for you
                </div>
                <div className="space-y-3">
                  {personalizedQuestions.reflection.map((q) => (
                    <button
                      key={q.id}
                      onClick={() => setPrompt(q.question)}
                      className="w-full text-left p-3 rounded-lg bg-white/80 hover:bg-white border border-purple-100 hover:border-purple-300 transition-all group"
                    >
                      <p className="text-sm font-medium group-hover:text-purple-700 transition-colors">{q.question}</p>
                      {q.subtext && (
                        <p className="text-xs text-muted-foreground mt-1">{q.subtext}</p>
                      )}
                      {q.followUp && prompt === q.question && (
                        <p className="text-xs text-purple-600 mt-2 italic">💡 {q.followUp}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-base">Daily Reflection</Label>

              <ReflectionPrompt
                onPromptSelect={setPrompt}
                selectedPrompt={prompt}
                userPersona={user?.persona}
                recentMood={mood}
                enableAI={true}
              />

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reflection-input" className="text-sm font-medium">
                    Write your reflection here:
                  </Label>
                  <textarea
                    id="reflection-input"
                    placeholder="Type your reflection here..."
                    value={reflection}
                    onChange={(e) => {
                      setUserHasTypedReflection(true);
                      setReflection(e.target.value);
                    }}
                    className="w-full min-h-[150px] p-3 border-2 border-gray-300 dark:border-gray-600 rounded-md focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none resize-none bg-background dark:bg-gray-800/50 text-foreground dark:text-gray-100 placeholder:text-muted-foreground dark:placeholder:text-gray-400"
                    style={{ fontSize: '16px' }}
                  />
                </div>
                
                {/* AI Analysis Results */}
                {isAnalyzingReflection && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 text-blue-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">AI is analyzing your reflection...</span>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {insights && !isAnalyzingReflection && (
                  <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200 shadow-lg">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <div className="w-3 h-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-pulse"></div>
                        ✨ AI Insights from Your Reflection
                      </CardTitle>
                      <CardDescription className="text-xs text-purple-700 font-medium">
                        Personalized analysis based on your patterns and check-in history
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm font-medium mb-1">Sentiment Score:</p>
                        <div className="flex items-center gap-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                insights.sentimentScore > 0.5 ? 'bg-green-500' : 
                                insights.sentimentScore > 0 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.max(0, (insights.sentimentScore + 1) * 50)}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600">
                            {insights.sentimentScore > 0.5 ? 'Positive' : 
                             insights.sentimentScore > 0 ? 'Neutral' : 'Negative'}
                          </span>
                        </div>
                      </div>
                      
                      {insights.keyThemes.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Key Themes:</p>
                          <div className="flex flex-wrap gap-2">
                            {insights.keyThemes.map((theme, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {theme}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {insights.insightSummary && (
                        <div>
                          <p className="text-sm font-medium mb-1">Summary:</p>
                          <p className="text-sm text-gray-700">{insights.insightSummary}</p>
                        </div>
                      )}
                      
                      {insights.focusAreas && insights.focusAreas.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Focus Areas:</p>
                          <ul className="text-sm text-gray-700 space-y-1">
                            {insights.focusAreas.map((area, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <div className="w-1 h-1 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                                {area}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {insights.suggestedActions && insights.suggestedActions.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Suggested Actions:</p>
                          <ul className="text-sm text-gray-700 space-y-1">
                            {insights.suggestedActions.map((action, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <div className="w-1 h-1 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between">
        {step > 1 ? (
          <Button
            type="button"
            variant="outline"
            onClick={prevStep}
            disabled={isSubmitting}
          >
            Back
          </Button>
        ) : (
          <div></div> // Empty div to maintain spacing
        )}
        <Button onClick={nextStep} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {step < 3 ? "Processing..." : "Submitting..."}
            </>
          ) : step < 3 ? (
            "Continue"
          ) : (
            "Submit Check-in"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default EnhancedCheckIn;
